import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { CSSProperties, useEffect, useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { IOptionsProps, IOptionProps } from '../../../../@types/reactSelect/IOption'
import { ICoords } from '../../../../@types/GoogleMaps/ICoord'
import { Input } from '../../components/Input'
import { InputIcon } from '../../components/InputIcon'
import { Dropdown } from '../../components/Select'
import { FooterEditOnboarding } from '../../Footer'
import { DrawOnMap } from '../../components/DrawOnMap/DrawOnMap'
import { ReactComponent as Available } from '../../../../assets/svg/icons/available.svg'
import { ReactComponent as Unvailable } from '../../../../assets/svg/icons/unavailable.svg'
import { Chips } from '../../../../components/Chips'
import { AlertModal } from '../../../../components/Modal/Alert/Index'
import { SimpleButtonClose } from '../../../../components/Modal/Contents/SimpleButtonClose/Index'
import { TextModal } from '../../../../components/Modal/Contents/TextModal/Index'
import { TitleModal } from '../../../../components/Modal/Contents/TitleModal/Index'
import { ButtonRedirect } from '../../../../components/Modal/Error/ButtonRedirect/Index'
import { moveCharLeft } from '../../../../utils/arrays/moveChar'
import { sortBy } from '../../../../utils/arrays/sortBy'
import { calculateHectares } from '../../../../utils/geolib/calculateHectares'
import { areaService } from '../../../../services/areaService'
import { microAreaService } from '../../../../services/microAreaService'
import { farmService } from '../../../../services/farmService'
import { userService } from '../../../../services/userService'
import Constants from '../../../../constants/Index'
import { useEditOnboarding } from '../../../../hooks/useEditOnboarding'
import './styles.css'


interface IFormAreaData {
  farm: IOptionProps | null
  name: string
  initials: string
  acreage: string
  grazing_time: {
    indoor: IOptionProps | null
    outdoor: IOptionProps | null
  }
  owner_user_id: IOptionProps | null
  map_coords: ICoords
}

export function Area() {
  const areaSchema = yup.object().shape({
    farm: yup
      .object({
        label: yup.string(),
        value: yup.string().required('Fazenda é necessária.'),
      })
      .nullable()
      .required('Fazenda é necessária.'),
    name: yup
      .string()
      .required('Campo obrigatório não informado.'),
    initials: yup
      .string()
      .matches(/^\S+(\s+\S+)*$/, 'Esta sigla não é válida.')
      .required('Sigla é necessário.'),
    acreage: yup
      .string()
      .test('value', 'Campo obrigatório não informado. ', () => {
        if (Number(acreageInput?.replace(',', '.')) === 0) return false
        return true
      })
      .test(
        'value',
        'O espaço disponível nesta fazenda foi ultrapassado',
        () => {
          if (Number(remainingArea) >= 0) return true
          return false
        },
      )
      .required('Campo obrigatório não informado.'),
    grazing_time: yup.object({
      indoor: yup
        .object({
          label: yup.string(),
          value: yup.string(),
        })
        .nullable()
        .required('Campo obrigatório não informado.'),
      outdoor: yup
        .object({
          label: yup.string(),
          value: yup.string(),
        })
        .nullable()
        .required('Campo obrigatório não informado.'),
    }),
    owner_user_id: yup
      .object({
        label: yup.string(),
        value: yup.string(),
      })
      .nullable()
      .required('Campo obrigatório não informado.'),
    map_coords: yup
      .array()
      .of(
        yup.object({
          lat: yup.number(),
          lng: yup.number(),
        }),
      )
      .test('is-null', 'Selecione a fazenda para realizar o desenho da área no mapa.', (value) =>
        value ? value.length > 0 : true,
      ),
  })

  const { setOptionForm, defaultValue } = useEditOnboarding()
  const isNew = !defaultValue
  const [listFarms, setListFarms] = useState<IOptionsProps>()
  const [managersList, setManagersList] = useState<IOptionsProps>()
  const [currentFarm, setCurrentFarm] = useState<any>()
  const [currentInitials, setCurrentInitials] = useState<string>('')
  const [initialsOnlyRead, setInitialsOnlyRead] = useState<boolean>(true)
  const isInitialsReadOnly = !isNew || initialsOnlyRead
  const [msgError, setMsgError] = useState<string>('')
  const [submiting, setSubmiting] = useState(false)
  const [areaCoords, setAreaCoords] = useState<ICoords>([])
  const [staticAreas, setStaticAreas] = useState<ICoords[]>([])
  const [microAreasExisting, setMicroAreasExisting] = useState<
    { id: number; name: string }[]
  >([])
  const [remainingArea, setRemainingArea] = useState<string>('')
  const [parentAcreage, setParentAcreage] = useState<string>('')
  const [occupiedArea, setOccupiedArea] = useState<number>(0)

  const formDefaultValues = useMemo(() => {
    if (!defaultValue) {
      return {
        farm: null,
        name: '',
        initials: '',
        acreage: '',
        grazing_time: {
          indoor: null,
          outdoor: null,
        },
        owner_user_id: null,
        map_coords: []
      }
    }
    
    const farm = listFarms?.find(f => 
      f.value === defaultValue.farm_id || 
      f.info?.initials === defaultValue.farm_initials
    ) || null;
    const manager = managersList?.find(m => m.value === defaultValue.manager_user_id) || null;
      
    return {
      farm,
      name: defaultValue.name || '',
      initials: defaultValue.initials || '',
      acreage: defaultValue.acreage || '',
      grazing_time: {
        indoor: {
          value: defaultValue.indoor_grazing_time?.toString(),
          label: `${defaultValue.indoor_grazing_time} Dias`,
        },
        outdoor: {
          value: defaultValue.outdoor_grazing_time?.toString(),
          label: `${defaultValue.outdoor_grazing_time} Dias`,
        },
      },
      owner_user_id: manager,
      map_coords: defaultValue.map_coords || []
    }
  }, [defaultValue, listFarms, managersList])

  const contentStyleAlert = {
    width: '335px',
    height: '237px',
    borderRadius: '20px',
    padding: '20px',
  } as CSSProperties

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<IFormAreaData>({
    defaultValues: formDefaultValues,
    resolver: yupResolver(areaSchema),
  })

  const acreageInput = watch('acreage')
  const initialsInput = watch('initials')
  const mapCoordsInput = watch('map_coords')
  const nameInput = watch('name')
  const farmInput = watch('farm')

  const onSubmit = (data: any) => {
    setSubmiting(true)
    
    var acreageFix = data?.acreage?.replace(',', '.')
    
    if (isNew) {
      var formNewArea = [
        {
          name: data?.name,
          initials: data?.initials,
          acreage: moveCharLeft(acreageFix, acreageFix.charAt(2)),
          farm_id: data?.farm?.value,
          indoor_grazing_time: parseInt(data?.grazing_time?.indoor?.value),
          outdoor_grazing_time: parseInt(data?.grazing_time?.outdoor?.value),
          owner_user_id: data?.owner_user_id?.value,
          map_coords: data?.map_coords,
        },
      ]
      areaService
        .createNewArea(formNewArea)
        .then(() => {
          setOptionForm(undefined)
          setMsgError('')
        })
        .catch((error) => setMsgError(error.response.data.msg))
        .finally(() => {
          setSubmiting(false)
        })
    } else {
      var formEditArea = [
        {
          area_id: defaultValue?.id || -1,
          name: data?.name,
          initials: data?.initials,
          acreage: moveCharLeft(acreageFix, acreageFix.charAt(2)),
          indoor_grazing_time: parseInt(data?.grazing_time?.indoor?.value),
          outdoor_grazing_time: parseInt(data?.grazing_time?.outdoor?.value),
          owner_user_id: parseInt(data?.owner_user_id?.value),
        },
      ]
      areaService
        .editArea(formEditArea)
        .then(() => setOptionForm(undefined))
        .catch((error) => setMsgError(error.response.data.msg))
        .finally(() => {
          setSubmiting(false)
        })
    }
  }

  const deleteArea = () => {
    if (defaultValue?.id) {
      areaService
        .deleteArea(defaultValue.id)
        .then(() => setOptionForm(undefined))
        .catch((error) => setMsgError(error.response.data.msg))
    }
  }

  useEffect(() => {
    userService
      .getUsersByOffice(Constants.PROFILE_CODE_CAPATAZ.toString())
      .then((data) => {
        setManagersList(
          data.map((user: any) => ({
            value: user.id,
            label: user.name,
          })),
        )
      })

    // Carregar fazendas sempre (para dropdown)
    farmService.getFarmsByUser().then((data) => {
      setListFarms(
        data.map((farm: any) => ({
          label: farm.name,
          value: farm.id,
          info: farm,
        })),
      )
    })
  }, [defaultValue])


  useEffect(() => {
    if (listFarms && managersList) {
      reset(formDefaultValues)
    }
  }, [defaultValue, listFarms, managersList, reset])

  useEffect(() => {
    if (!isNew && defaultValue && listFarms && listFarms.length > 0) {
      const farm = listFarms.find(f => 
        f.value === defaultValue.farm_id || 
        f.info?.initials === defaultValue.farm_initials
      )
      if (farm) {
        setValue('farm', farm)

        setTimeout(() => {
          setCurrentFarm(farm.info)
        }, 300);
      }
    }
  }, [isNew, defaultValue, listFarms, setValue])

  useEffect(() => {
    if (currentFarm) {
      setCurrentInitials(`${currentFarm?.initials}`)
    }
  }, [currentFarm])

  useEffect(() => {
    if (isNew && nameInput && initialsOnlyRead) {
      const arrayWords = nameInput.split(' ')
      if (arrayWords.length > 1) {
        const firstSentence = arrayWords[0].charAt(0)
        let secondSentence = ''
        if (arrayWords[1].length > 1)
          secondSentence = arrayWords[1].slice(0, 2)
        else
          secondSentence = `${arrayWords[1].charAt(0)}${arrayWords[0].charAt(1)}`
        setValue(
          'initials',
          `${firstSentence}${secondSentence}`.toLocaleUpperCase(),
        )
      } else
        setValue(
          'initials',
          `${arrayWords[0]}`.slice(0, 3).padEnd(3, '').toLocaleUpperCase(),
        )
    } else if (isNew && !nameInput && initialsOnlyRead) setValue('initials', '')
  }, [isNew, nameInput, initialsOnlyRead, setValue])

  useEffect(() => {
    if (isNew && !!initialsInput) setValue('initials', initialsInput.toLocaleUpperCase())
  }, [isNew, initialsInput, setValue])

  // Calcula hectares automaticamente quando usuário desenha no mapa
  useEffect(() => {
    if (mapCoordsInput && mapCoordsInput.length >= 3 && (!acreageInput || acreageInput === '0.00')) {
      const calculatedAcreage = calculateHectares(mapCoordsInput);
      setValue('acreage', calculatedAcreage);
    }
  }, [mapCoordsInput, acreageInput, setValue])

  // Calcula área da fazenda
  useEffect(() => {
    if (currentFarm && currentFarm.map_coords && currentFarm.map_coords.length >= 3) {
      const farmArea = calculateHectares(currentFarm.map_coords)
      setParentAcreage(farmArea)
    }
  }, [currentFarm])

  // Calcula área restante da fazenda quando acreage muda
  useEffect(() => {
    if (parentAcreage) {
      const farmArea = Number(parentAcreage)
      const currentAreaValue = Number(acreageInput?.replace(',', '.') || 0)
      const available = farmArea - occupiedArea - currentAreaValue
      setRemainingArea(available.toFixed(2))
    }
  }, [parentAcreage, acreageInput, occupiedArea])

  useEffect(() => {
    if (currentFarm) {
      setAreaCoords([...currentFarm.map_coords, currentFarm.map_coords[0]])
      
      // Buscar áreas existentes da farm para exibir no mapa e calcular área ocupada
      areaService.getAreabyFarmId(currentFarm.id).then((data) => {
        // Filtrar áreas para exibir no mapa (excluindo a área sendo editada)
        const otherAreas = data.filter((area) => area.id !== defaultValue?.id)
        setStaticAreas(otherAreas.map((area) => area.map_coords))
        
        // Calcular área total ocupada
        const acreageUsed = otherAreas
          .reduce((sum, current) => sum + Number(current.acreage), 0)
        setOccupiedArea(acreageUsed)
      })
    } else {
      setAreaCoords([])
      setStaticAreas([])
      setOccupiedArea(0)
    }
  }, [currentFarm, defaultValue?.id])


  useEffect(() => {
    if (defaultValue?.id) {
      microAreaService.getMicroAreasByAreaId(defaultValue.id).then((data) => {
        const microExistenting: { id: number; name: string }[] = []
        const microCoordsArray: ICoords[] = []
        data.map(({ id, farm, area, initials, map_coords }) => {
          microExistenting.push({
            id,
            name: `${farm.initials}-${area.initials}-${initials}`,
          })
          microCoordsArray.push(map_coords as ICoords)
        })
        setMicroAreasExisting(microExistenting.sort(sortBy('name')))
      })
    } else {
      setMicroAreasExisting([])
    }
  }, [defaultValue])

  return (
    <>
      <AlertModal visible={!!msgError} contentStyle={contentStyleAlert}>
        <section className="EditAreas_modalalert-container">
          <SimpleButtonClose onClick={() => setMsgError('')} />
          <main className="EditAreas_modalalert-content">
            <TitleModal>Algo deu errado.</TitleModal>
            <TextModal>{msgError}</TextModal>
          </main>
          <ButtonRedirect onClick={() => setMsgError('')}>
            Voltar
          </ButtonRedirect>
        </section>
      </AlertModal>
      <main className={'body__formEditOnboarding'}>
        <div className="firstColumn">
          <form onSubmit={handleSubmit(onSubmit)}>
              <Dropdown
                control={control}
                label={'Fazenda'}
                placeholderText={'Selecione...'}
                options={listFarms}
                getSelectedData={(option: any) => {
                  setCurrentFarm(option.info)
                }}
                closeMenuOnSelect={true}
                errorText={(errors?.farm as any)?.message}
                name={'farm'}
                isDisabled={!isNew}
              />

            <Input
              control={control}
              label={'Nome do Retiro'}
              theme={'normal'}
              errorText={errors?.name?.message as any}
              name={'name'}
            />

            <Input
              control={control}
              label={'Sigla'}
              theme={isInitialsReadOnly ? 'read-only' : 'normal'}
              disabled={isInitialsReadOnly}
              labelPrefix={`${isNew
                  ? currentInitials
                    ? `${currentInitials}-`
                    : ''
                  : `${defaultValue?.farm_initials}-`
                }`}
              overrideStyles={{
                input: {
                  width: 200,
                },
              }}
              maxLength={4}
              errorText={errors?.initials?.message as any}
              name={'initials'}
            />

            <label
              onClick={() => isNew && setInitialsOnlyRead(!initialsOnlyRead)}
              className="initial--readOnly"
              hidden={!isNew}
            >
              {initialsOnlyRead ? 'Alterar sigla' : 'Usar sigla automática'}
            </label>

            <div>
              <label className="acreageLabel">
                Quantos hectares de área útil?
              </label>

              <div className="hectares__container">
                <Input
                  name={'acreage'}
                  theme={'normal'}
                  label={''}
                  type={'number'}
                  control={control}
                  disabled={false}
                  prefixPosition="right"
                  labelPrefix="ha"
                  overrideStyles={{
                    prefixLabel: {
                      color: '#999999',
                    },
                    input: {
                      width: 120,
                      maxWidth: 200,
                    },
                    contentInput: {
                      width: 200,
                      marginRight: 20,
                    },
                  }}
                  errorText={(errors?.acreage as any)?.message ? ' ' : ''}
                />

                <InputIcon
                  theme={'normal'}
                  control={control}
                  disabled={true}
                  className={'microarea__container__icon'}
                  labelPrefix={`${remainingArea} disponíveis`}
                  Icon={Number(remainingArea) >= 0 ? Available : Unvailable}
                  prefixPosition={'right'}
                  name={'remainingArea'}
                  overrideStyles={{
                    prefixLabel: {
                      maxWidth: 200,
                      minWidth: 200,
                    },
                    input: {
                      width: 10,
                    },
                    container: {
                      width: 280,
                    },
                  }}
                />
              </div>
            </div>

            {errors?.acreage ? (
              <small className="textInput--small-error animation__fadeIn">
                {(errors?.acreage as any)?.message}
              </small>
            ) : (
              ''
            )}

            <Dropdown
              label={'Tempo Máximo COM gado'}
              closeMenuOnSelect={true}
              control={control}
              options={Constants.DAYS_OPTIONS}
              errorText={(errors?.grazing_time as any)?.indoor?.message}
              name={'grazing_time.indoor'}
              placeholderText={'Selecione...'}
            />

            <Dropdown
              label={'Tempo Máximo SEM gado'}
              closeMenuOnSelect={true}
              control={control}
              options={Constants.DAYS_OPTIONS}
              errorText={(errors?.grazing_time as any)?.outdoor?.message}
              name={'grazing_time.outdoor'}
              placeholderText={'Selecione...'}
            />

            <Dropdown
              label={'Responsável do Retiro'}
              closeMenuOnSelect={true}
              control={control}
              options={managersList}
              errorText={(errors?.owner_user_id as any)?.message}
              name={'owner_user_id'}
              placeholderText={'Selecione...'}
            />
          </form>
        </div>
        <div className="secondColumn">
          <div className="draw-on-map-container">
            <form onSubmit={handleSubmit(onSubmit)}>
              <DrawOnMap
                staticsElements={staticAreas}
                style={{ width: '400px', height: '300px' }}
                mainPolygon={[]}
                initialsLabel={`${currentInitials}-${initialsInput || ''}`}
                control={control}
                fitCoords={currentFarm?.map_coords}
                name={'map_coords'}
                editable={isNew && currentFarm}
              />
            </form>
          </div>
          <div className="microareas-list">
            <ul>
              {microAreasExisting.map((area) => (
                <li key={area.id}>
                  <Chips.Pressable
                    variationStyles={{ container: { width: '100%' } }}
                    theme={'link-light'}
                    label={area.name}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </main>

      <FooterEditOnboarding
        undoFunction={() => setOptionForm(undefined)}
        saveFunction={handleSubmit((data)=>{
          if(!submiting){
            onSubmit(data)
          }
        })}
        deleteFunction={deleteArea}
        isNew={isNew}
        disabledSubmit={submiting}
      />
    </>
  )
}
